
import { Vue, Component, Prop } from "vue-property-decorator";
import { Contractor } from "@/generated/graphql";
import CardBodyItem from "@/components/card/CardBodyItemNew.vue";
import Card from "@/components/card/ResourceCards.vue";
import TableIcon from "@/components/table/table-icon.vue";
import Chip from "@/components/filters/Chip.vue";
import LocationIconNew from "@/components/icons/LocationIconNew.vue";
import UserIcon from "@/components/icons/user.vue";
import CalendarIcon from "@/components/icons/calendar.vue";
import DollarIcon from "@/components/icons/dollar.vue";
import ProfileIcon from "@/components/icons/profile.vue";
import RouteNames from "@/entities/RouteNames";
import Processing from "@/components/common/Processing.vue";
import UiButton from "@/components/button/ui-button.vue";
import UPDATE_CONTRACTOR_LIGHT from "@/apollo/contractors/UpdateContractorLight.gql";
import ResourceAvatar from "@/components/card/ResourceAvatar.vue";
import ItemChipsWithMore from "@/components/common/ItemChipsWithMore.vue";
import RouteParams from "@/entities/RouteParams";
import { NO_INFORMATION } from "@/data/placeholders";

@Component({
  components: {
    CardBodyItem,
    Card,
    TableIcon,
    LocationIconNew,
    CalendarIcon,
    DollarIcon,
    ProfileIcon,
    UserIcon,
    Chip,
    UiButton,
    Processing,
    ResourceAvatar,
    ItemChipsWithMore,
  },
})
export default class ArchivedRequestContractorCard extends Vue {
  @Prop({ type: Object, required: true }) readonly contractor!: Contractor;
   @Prop({ type: Boolean, required: true }) readonly contractorApproved!: boolean;
  processing: boolean = false;
  nameProvided: boolean = false;
  routes = RouteNames;

  get contractorName(): string | undefined {
    const name = this.contractor.user.given_name?.trim();
    const surname = this.contractor.user.family_name?.trim();
    if (!name && !surname) {
      return undefined;
    }
    this.nameProvided = true;
    return `${name} ${surname}`;
  }

  get contractorDetailsLink(): RouteParams<{ id: string }> {
    return {
      name: this.routes.CONTRACTOR_PUBLIC_PAGE,
      params: { id: this.contractor.user.id },
    };
  }

  get contractorDate(): string {
    if (this.contractor.created_at && this.contractor.created_at !== null) {
      const joinedDate = new Date(this.contractor.created_at);
      const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

      const year = joinedDate.getFullYear();
      const month = joinedDate.getMonth();

      const formattedDate = `${monthNames[month]} ${year}`;

      return formattedDate;
    }
    return NO_INFORMATION;
  }

  get contractorInitials(): string {
    return `${this.contractor.user.given_name![0]}${
      this.contractor.user.family_name![0]
    }`;
  }

  get contractorTitle(): string {
    return (
      `${this.contractor.user.given_name} ${this.contractor.user.family_name}` ||
      NO_INFORMATION
    );
  }
  get contractorSatus(): string {
    return this.contractor.user.confirmed ? "Confirmed" : "Not confirmed";
  }

  get contractorRates(): string | undefined {
    const minRate = this.contractor.user.profile.min_rate;
    const maxRate = this.contractor.user.profile.max_rate;
    const currency = this.contractor.user.profile.currency;
    const rateUnit = this.contractor.user.profile.rate_unit;
    if (!minRate && !maxRate) {
      return NO_INFORMATION;
    }

    if (!maxRate) {
      return `${minRate} ${currency}/${rateUnit}`;
    }

    if (!minRate) {
      return `${maxRate} ${currency}/${rateUnit}`;
    }

    return `${minRate}-${maxRate} ${currency}/${rateUnit}`;
  }

  get contractorAvatar(): string | undefined {
    return this.contractor.user.avatar || undefined;
  }

  get userAvailable(): boolean {
    return this.contractor.user.available;
  }

  get displaySkills(): boolean {
    return !!this.contractorSkills?.length;
  }

  get contractorLocation(): string {
    if (!this.contractor.user.profile) {
      return NO_INFORMATION;
    }
    const { country, state, city } = this.contractor.user.profile;
    if (!country && !state && !city) {
      return NO_INFORMATION;
    }
    return `${city}, ${state}, ${country}`;
  }

  get contractorSkills(): any[] {
    if (this.contractor.user.profile?.skills?.length) {
      return this.contractor.user.profile.skills.reduce(
        (acc: any[], { skill }) => {
          if (skill.name) {
            acc.push({ name: skill.name, id: skill.id });
          }
          return acc;
        },
        []
      );
    }
    return [];
  }

  get navigateToDetails(): Object {
    return {
      name: RouteNames.CONTRACTOR_DETAILS_ABOUT,
      params: { id: this.contractor.id },
    };
  }

  get profileConfirmed(): string {
    return this.contractor.user.confirmed ? "confirmed" : "not confirmed";
  }

  get emailFontSize(): string {
    return this.nameProvided ? "text-sm" : "text-lg";
  }

  async handleRestoreContractor() {
    this.processing = true;
    await this.restoreContractor(this.contractor.id);
    this.$emit("refetchRequestsContractorsNumber");
    this.$emit("refetchArchived");
    this.processing = false;
  }

  restoreContractor(contractorId: string) {
    return this.$apollo.mutate({
      mutation: UPDATE_CONTRACTOR_LIGHT,
      variables: {
        contractorId,
        set: {
          deleted: false,
          approved: true,
        },
      },
    });
  }
}
